import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { Card, CardContent } from "@/components/ui/card";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      {/* Header */}
      <section className="primary-gradient text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-indigo-100">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8">
              <div className="prose prose-lg max-w-none space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                  <p>
                    PrizeDrop (Pty) Ltd ("we," "us," or "our") is committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our online and mobile promotional competition platform ("the Platform"). This policy is prepared in compliance with South Africa's Protection of Personal Information Act, 2013 (POPIA) and other applicable data protection laws.
                  </p>
                  <p>
                    By using our Platform, you consent to the collection and use of your information as described in this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Platform.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                  <h3 className="text-xl font-semibold mb-2">Personal Information</h3>
                  <p>We collect personal information that you provide directly to us, including:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Identity Information:</strong> Name, date of birth, gender, ID number (for age verification)</li>
                    <li><strong>Contact Information:</strong> Email address, phone number, postal address</li>
                    <li><strong>Account Information:</strong> Username, password, profile picture</li>
                    <li><strong>Demographic Information:</strong> Location, interests, preferences, family details</li>
                    <li><strong>Competition Entries:</strong> Answers to questions, submitted content, social media handles</li>
                    <li><strong>Financial Information:</strong> Banking details (for prize distribution only)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-6">Automatically Collected Information</h3>
                  <p>When you access our Platform, we may automatically collect:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
                    <li><strong>Usage Data:</strong> Pages visited, time spent, features used, competition participation</li>
                    <li><strong>Location Data:</strong> General geographic location (with your permission)</li>
                    <li><strong>Cookies and Tracking:</strong> Information stored through cookies and similar technologies</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
                  <p>We use the personal information we collect for the following purposes:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Competition Administration:</strong> Managing your entries, conducting draws, verifying winners, distributing prizes</li>
                    <li><strong>Account Management:</strong> Creating and maintaining your account, providing customer support</li>
                    <li><strong>Platform Improvement:</strong> Analyzing usage patterns, improving features, developing new services</li>
                    <li><strong>Communication:</strong> Sending notifications about competitions, winners, platform updates, and marketing (with consent)</li>
                    <li><strong>Legal Compliance:</strong> Meeting regulatory requirements, maintaining records, preventing fraud</li>
                    <li><strong>Personalization:</strong> Customizing your experience, showing relevant competitions and offers</li>
                    <li><strong>Research and Analytics:</strong> Understanding user behavior, measuring platform performance</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">4. Legal Basis for Processing</h2>
                  <p>We process your personal information based on the following legal grounds:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Consent:</strong> Where you have given clear consent for specific processing activities</li>
                    <li><strong>Contract Performance:</strong> Where processing is necessary to fulfill our Terms and Conditions</li>
                    <li><strong>Legal Obligation:</strong> Where we must process data to comply with legal requirements</li>
                    <li><strong>Legitimate Interest:</strong> Where we have a legitimate business interest that doesn't override your rights</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">5. Information Sharing and Disclosure</h2>
                  <p>We respect your privacy and will not sell or rent your personal information to third parties. However, we may share your information in the following circumstances:</p>
                  
                  <h3 className="text-xl font-semibold mb-2 mt-4">With Your Consent</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Third-party sponsors and partners for marketing purposes (opt-in only)</li>
                    <li>Social media platforms when you choose to share content</li>
                    <li>Prize providers for fulfillment purposes</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">For Legal and Business Purposes</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Independent auditors for competition verification</li>
                    <li>Legal authorities when required by law or to protect our rights</li>
                    <li>Service providers who assist in platform operations (under strict confidentiality)</li>
                    <li>Business partners for competition administration</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">Public Information</h3>
                  <p>With your consent, we may publicly share:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Winner announcements (first name, last initial, general location)</li>
                    <li>User-generated content submitted to competitions</li>
                    <li>Testimonials and reviews (with permission)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
                  <p>
                    We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Technical Safeguards:</strong> Encryption, secure servers, firewalls, access controls</li>
                    <li><strong>Organizational Safeguards:</strong> Employee training, confidentiality agreements, access restrictions</li>
                    <li><strong>Regular Reviews:</strong> Continuous monitoring and updating of security practices</li>
                    <li><strong>Incident Response:</strong> Procedures for handling and reporting data breaches</li>
                  </ul>
                  <p className="mt-4">
                    Please note that no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
                  <p>
                    We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, or as required by law. Specific retention periods include:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Competition Records:</strong> At least 3 years after competition end (legal requirement)</li>
                    <li><strong>Account Information:</strong> Until account deletion or 2 years of inactivity</li>
                    <li><strong>Marketing Data:</strong> Until consent is withdrawn</li>
                    <li><strong>Legal Claims:</strong> Until statute of limitations expires</li>
                  </ul>
                  <p className="mt-4">
                    After the retention period, we will securely delete or anonymize your personal information unless we have a legal obligation to keep it longer.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">8. Your Privacy Rights</h2>
                  <p>Under POPIA and other applicable laws, you have the following rights regarding your personal information:</p>
                  
                  <h3 className="text-xl font-semibold mb-2 mt-4">Access Rights</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Request a copy of the personal information we hold about you</li>
                    <li>Receive information about how we process your data</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">Correction and Update</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Correct inaccurate or incomplete information</li>
                    <li>Update your profile information at any time</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">Deletion Rights</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Request deletion of your personal information</li>
                    <li>Close your account and associated data</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">Processing Rights</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Object to certain types of processing</li>
                    <li>Restrict processing in specific circumstances</li>
                    <li>Data portability for information you provided</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-2 mt-4">Consent Management</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Withdraw consent for marketing communications</li>
                    <li>Opt out of data sharing with third parties</li>
                    <li>Manage cookie preferences</li>
                  </ul>

                  <p className="mt-4">
                    To exercise any of these rights, please contact our Data Protection Officer using the contact information provided below. We may require identity verification before processing your request.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">9. Cookies and Tracking Technologies</h2>
                  <p>
                    Our Platform uses cookies and similar tracking technologies to enhance your experience. Cookies are small text files stored on your device that help us:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Remember your login session and preferences</li>
                    <li>Analyze website traffic and usage patterns</li>
                    <li>Personalize content and advertisements</li>
                    <li>Improve platform functionality and performance</li>
                  </ul>
                  <p className="mt-4">
                    You can manage cookie preferences through your browser settings. However, disabling certain cookies may affect platform functionality. For mobile apps, we may use device identifiers for analytics and push notifications with your permission.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
                  <p>
                    PrizeDrop primarily stores and processes data on servers located in Sub-Saharan Africa, with a focus on South Africa. However, some of our service providers may be located in other countries. When personal information is transferred internationally, we ensure adequate protection through:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Standard contractual clauses approved by data protection authorities</li>
                    <li>Adequacy decisions recognizing equivalent protection levels</li>
                    <li>Certification schemes ensuring appropriate safeguards</li>
                  </ul>
                  <p className="mt-4">
                    By using our services, you acknowledge and consent to such transfers, subject to these safeguards.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">11. Children's Privacy</h2>
                  <p>
                    Our Platform is not intended for children under 18 years of age. We do not knowingly collect personal information from minors. Our registration process includes age verification to prevent underage sign-ups. If we become aware that we have collected personal information from a child under 18, we will delete such information immediately.
                  </p>
                  <p className="mt-4">
                    Parents or guardians who believe we may have collected information about a minor should contact us immediately so we can investigate and remove the information.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">12. Marketing Communications</h2>
                  <p>
                    With your consent, we may send you marketing communications about:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>New competitions and prize opportunities</li>
                    <li>Platform updates and new features</li>
                    <li>Special promotions and partner offers</li>
                    <li>Winner announcements and success stories</li>
                  </ul>
                  <p className="mt-4">You can opt out of marketing communications at any time by:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Clicking the unsubscribe link in any email</li>
                    <li>Updating your preferences in your account settings</li>
                    <li>Contacting our customer support team</li>
                  </ul>
                  <p className="mt-4">
                    Note that even if you opt out of marketing communications, we may still send you important transactional messages related to your account and competition participation.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">13. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Post a prominent notice on our Platform</li>
                    <li>Send email notifications to registered users</li>
                    <li>Update the "Last updated" date at the top of this policy</li>
                    <li>Provide reasonable notice before changes take effect</li>
                  </ul>
                  <p className="mt-4">
                    Your continued use of the Platform after changes become effective constitutes acceptance of the updated Privacy Policy. We encourage you to review this policy periodically to stay informed about how we protect your information.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">14. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy, wish to exercise your privacy rights, or need to report a privacy concern, please contact us:
                  </p>
                  <div className="mt-4 space-y-2">
                    <p><strong>Data Protection Officer:</strong></p>
                    <p><strong>Email:</strong> privacy@prizedrop.co.za</p>
                    <p><strong>General Support:</strong> support@prizedrop.co.za</p>
                    <p><strong>Website:</strong> www.prizedrop.co.za</p>
                  </div>
                  <p className="mt-4">
                    Please include your name, contact information, and a detailed description of your request or concern. We will respond to your inquiry as soon as reasonably possible, typically within 30 days.
                  </p>
                  <p className="mt-4">
                    If you are not satisfied with our response to your privacy concern, you may also contact the South African Information Regulator at:
                  </p>
                  <div className="mt-2 space-y-1">
                    <p><strong>Information Regulator (South Africa)</strong></p>
                    <p><strong>Website:</strong> www.justice.gov.za/inforeg</p>
                    <p><strong>Email:</strong> inforeg@justice.gov.za</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4">15. Opt-Out Instructions</h2>
                  <p>
                    If you wish to opt out of having your personal data shared with third-party advertisers, sponsors, or affiliates for marketing purposes, you can do so by:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Updating your privacy preferences in your account settings</li>
                    <li>Contacting our customer support team with your opt-out request</li>
                    <li>Using the opt-out links provided in marketing communications</li>
                  </ul>
                  <p className="mt-4">
                    Once you opt out, we will cease sharing your personal information with external partners for marketing purposes and stop sending you promotional communications. However, you may still receive transactional messages related to your account and any competitions you've entered.
                  </p>
                </section>

                <div className="border-t pt-6 mt-8">
                  <p className="text-sm text-muted-foreground">
                    This Privacy Policy is effective as of the date listed above and applies to all information collected by PrizeDrop through its website and mobile applications. Your privacy is important to us, and we are committed to protecting your personal information and being transparent about our data practices.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}